import React from 'react';
import { CheckCircle2, XCircle, Download } from 'lucide-react';
import { Button } from '../../ui/button';
import { ExportFileOptions, QAgentSuggestion } from '../../../services/QAgent';
import { ExportDocumentMenu } from './ExportDocumentMenu';
import { useI18n } from '../../../i18n';

interface SuggestionsSummaryProps {
  suggestions: QAgentSuggestion[];
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onViewSuggestion: (suggestion: QAgentSuggestion) => void;
  // Download a single file with its accepted suggestions (redline or clean)
  onExportFile?: (fileId: string, fileName: string, options: ExportFileOptions) => void;
  exportingFileId?: string | null;
  isLoading?: boolean;
}

interface FileGroup {
  fileId: string;
  fileName: string;
  suggestions: QAgentSuggestion[];
}

// Group suggestions by file (keyed by fileId) for clearer presentation
function groupByFile(suggestions: QAgentSuggestion[], fallbackName: string): FileGroup[] {
  const groups = new Map<string, FileGroup>();
  for (const s of suggestions) {
    const existing = groups.get(s.fileId);
    if (existing) {
      existing.suggestions.push(s);
    } else {
      groups.set(s.fileId, {
        fileId: s.fileId,
        fileName: s.fileName || fallbackName,
        suggestions: [s],
      });
    }
  }
  return Array.from(groups.values());
}

export const SuggestionsSummary: React.FC<SuggestionsSummaryProps> = ({
  suggestions,
  onAcceptAll,
  onRejectAll,
  onViewSuggestion,
  onExportFile,
  exportingFileId = null,
  isLoading = false,
}) => {
  const { t } = useI18n();
  const st = t.qagentDocument.suggestions;
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const acceptedCount = suggestions.filter(s => s.status === 'accepted').length;
  const rejectedCount = suggestions.filter(s => s.status === 'rejected').length;

  if (suggestions.length === 0) {
    return null;
  }

  // Group every still-relevant suggestion (pending or accepted) by file so that
  // each file offers a "download with edits" action - even once all of its
  // suggestions have been accepted and no pending items remain to list.
  const fileGroups = groupByFile(suggestions.filter(s => s.status !== 'rejected'), st.general);

  return (
    <div className="space-y-2">
      {/* Stats */}
      {(acceptedCount > 0 || rejectedCount > 0) && (
        <div className="flex gap-3 text-xs text-muted-foreground">
          {acceptedCount > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="h-3 w-3" />
              {st.acceptedCount(acceptedCount)}
            </span>
          )}
          {rejectedCount > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <XCircle className="h-3 w-3" />
              {st.rejectedCount(rejectedCount)}
            </span>
          )}
        </div>
      )}

      {/* Suggestions grouped by file, each with a download action */}
      {fileGroups.length > 0 && (
        <div className="space-y-2">
          {fileGroups.map(group => {
            const filePending = group.suggestions.filter(s => s.status === 'pending');
            const isExporting = exportingFileId === group.fileId;
            return (
              <div key={group.fileId} className="space-y-1">
                <div className="flex items-center gap-1 px-1.5">
                  <span
                    className="text-xs font-medium text-muted-foreground truncate flex-1 min-w-0"
                    title={group.fileName}
                  >
                    {group.fileName}
                  </span>
                  {onExportFile && (
                    <ExportDocumentMenu
                      fileName={group.fileName}
                      onExport={options => onExportFile(group.fileId, group.fileName, options)}
                    >
                      <Button
                        data-tour="suggestions-export"
                        variant="ghost"
                        size="sm"
                        disabled={isExporting}
                        title={st.downloadWithEditsTooltip}
                        className="h-6 px-1.5 text-xs text-brand hover:text-brand flex-shrink-0"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        {isExporting ? st.preparing : st.download}
                      </Button>
                    </ExportDocumentMenu>
                  )}
                </div>
                {filePending.slice(0, 3).map(suggestion => (
                  <button
                    key={suggestion.id}
                    onClick={() => onViewSuggestion(suggestion)}
                    className="w-full text-left p-1.5 rounded hover:bg-muted text-xs truncate"
                  >
                    <span className="capitalize font-medium">{st.types[suggestion.type] ?? suggestion.type}:</span>{' '}
                    <span className="text-muted-foreground">
                      {suggestion.sectionTitle || st.paragraph(suggestion.paragraphIndex + 1)}
                    </span>
                  </button>
                ))}
                {filePending.length > 3 && (
                  <div className="text-xs text-muted-foreground px-1.5">
                    {st.more(filePending.length - 3)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk actions */}
      {pendingSuggestions.length > 0 && (
        <div className="flex gap-1.5 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onAcceptAll}
            disabled={isLoading}
            className="flex-1 h-7 text-xs text-green-600 border-green-200 hover:bg-green-50"
          >
            {st.acceptAll}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRejectAll}
            disabled={isLoading}
            className="flex-1 h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
          >
            {st.rejectAll}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SuggestionsSummary;
